/**
 * tutorialReducer — the whole state machine, with no DOM and no side effects.
 *
 *   idle ──START──> active ──ADVANCE(last)──> finished
 *                    │  ▲
 *                 PAUSE  RESUME
 *                    ▼  │
 *                   paused
 *
 * Anything can EXIT back to idle. `stepCount` rides along on ADVANCE rather than
 * being read from a registry, so this file stays pure and trivially testable.
 */

export const TUTORIAL_STATUS = Object.freeze({
    IDLE: 'idle',
    ACTIVE: 'active',
    PAUSED: 'paused',
    FINISHED: 'finished',
});

export const initialState = Object.freeze({
    status: TUTORIAL_STATUS.IDLE,
    tutorialId: null,
    stepIndex: 0,
    /** Ids the tutorial has learned so far: scenarioId, startNodeId, choice1Id… */
    entities: {},
    /** Transient correction shown under the step body; cleared on every advance. */
    hint: null,
    /** True when the step's targets never appeared, so the UI can offer a retry. */
    resolveError: false,
});

export function tutorialReducer(state, action) {
    switch (action.type) {
        case 'START':
            return {
                ...initialState,
                status: TUTORIAL_STATUS.ACTIVE,
                tutorialId: action.tutorialId,
                stepIndex: action.stepIndex ?? 0,
                entities: action.entities ? { ...action.entities } : {},
            };

        case 'ADVANCE': {
            if (state.status !== TUTORIAL_STATUS.ACTIVE) return state;
            const next = state.stepIndex + 1;
            if (next >= action.stepCount) {
                return { ...state, status: TUTORIAL_STATUS.FINISHED, hint: null, resolveError: false };
            }
            return { ...state, stepIndex: next, hint: null, resolveError: false };
        }

        case 'GOTO': {
            if (state.status === TUTORIAL_STATUS.IDLE) return state;
            const index = Math.max(0, Math.min(action.index, action.stepCount - 1));
            if (index === state.stepIndex) return state;
            return { ...state, stepIndex: index, hint: null, resolveError: false };
        }

        case 'SET_ENTITY':
            if (!action.key) return state;
            return { ...state, entities: { ...state.entities, [action.key]: action.value } };

        case 'SET_ENTITIES':
            return { ...state, entities: { ...state.entities, ...(action.entities || {}) } };

        case 'SET_HINT':
            if (state.hint === action.hint) return state;
            return { ...state, hint: action.hint || null };

        case 'PAUSE':
            if (state.status !== TUTORIAL_STATUS.ACTIVE) return state;
            return { ...state, status: TUTORIAL_STATUS.PAUSED, resolveError: false };

        case 'RESUME':
            if (state.status !== TUTORIAL_STATUS.PAUSED) return state;
            return { ...state, status: TUTORIAL_STATUS.ACTIVE, resolveError: false };

        case 'RESOLVE_TIMEOUT':
            if (state.status !== TUTORIAL_STATUS.ACTIVE || state.resolveError) return state;
            return { ...state, resolveError: true };

        case 'RESOLVE_OK':
            if (!state.resolveError) return state;
            return { ...state, resolveError: false };

        case 'EXIT':
            return { ...initialState };

        default:
            return state;
    }
}

export default tutorialReducer;
